// Adds the saved ids to the options, option values and variants of a product
// update so PUT /products/:id/v2 updates them in place instead of recreating
// them (10x-api docs/products/update_v2.md).
//
// - Options keep the id they were loaded with, also when renamed. An option
//   without one takes the id of a saved option with the same name.
// - Option values and variants take the id of the saved entry with the same
//   value / option combination, so one removed and added back keeps its id.
// - Saved entries go first in id order and new ones last, which is the only
//   order v2 accepts. Variant options are reordered to match their options.

const sameValues = (a = [], b = []) =>
    a.length === b.length && a.every((value, index) => value === b[index]);

const savedFirst = (entries) => [
    ...entries.filter((entry) => entry.id).sort((a, b) => a.id - b.id),
    ...entries.filter((entry) => !entry.id),
];

const resolveOptionIds = (productOptions, savedOptions) => {
    const savedIds = new Set(savedOptions.map((option) => option.id));
    const used = new Set();
    const ids = productOptions.map((option) => {
        if (!option.id || !savedIds.has(option.id) || used.has(option.id)) return undefined;
        used.add(option.id);
        return option.id;
    });
    return ids.map((id, index) => {
        if (id) return id;
        const saved = savedOptions.find((option) =>
            !used.has(option.id) && option.name === productOptions[index].name
        );
        if (!saved) return undefined;
        used.add(saved.id);
        return saved.id;
    });
};

const resolveValues = (values, savedValues = []) => {
    const used = new Set();
    return values.map((value) => {
        const saved = savedValues.find((savedValue) =>
            !used.has(savedValue.id) && (savedValue.value || savedValue.name) === value.value
        );
        if (!saved) return value;
        used.add(saved.id);
        return { ...value, id: saved.id };
    });
};

// GET /products/:id returns each variant's option values in no fixed order.
// Put them back in the order of the product options that have values, which
// is how the variant table is built and how v2 maps them.
export const orderVariantOptions = (variantOptions = [], productOptions = []) => {
    const remaining = [...variantOptions];
    const ordered = productOptions
        .filter((option) => option.options && option.options.length)
        .map((option) => {
            const index = remaining.findIndex((value) =>
                option.options.some((optionValue) => (optionValue.value || optionValue.name) === value)
            );
            return index >= 0 ? remaining.splice(index, 1)[0] : undefined;
        });
    return ordered.every((value) => value !== undefined) && remaining.length === 0
        ? ordered
        : variantOptions;
};

export const withSavedIds = (productOptions, productVariants, savedProduct) => {
    const savedOptions = savedProduct?.product_options || [];
    const savedVariants = savedProduct?.product_variants || [];

    const optionIds = resolveOptionIds(productOptions, savedOptions);
    const resolvedOptions = productOptions.map((option, index) => {
        const id = optionIds[index];
        const saved = savedOptions.find((savedOption) => savedOption.id === id);
        return {
            ...option,
            id,
            sourceIndex: index,
            options: savedFirst(resolveValues(option.options, saved?.options)),
        };
    });
    const sortedOptions = savedFirst(resolvedOptions);

    // Variant options map positionally to the options that have values.
    const sourcePositions = productOptions
        .map((option, index) => (option.options.length ? index : -1))
        .filter((index) => index >= 0);
    const remap = (variantOptions = []) => sortedOptions
        .filter((option) => option.options.length)
        .map((option) => variantOptions[sourcePositions.indexOf(option.sourceIndex)])
        .filter((value) => value !== undefined);

    const savedVariantIds = new Set(savedVariants.map((variant) => variant.id));
    const used = new Set();
    const variantIds = productVariants.map((variant) => {
        if (!variant.id || !savedVariantIds.has(variant.id) || used.has(variant.id)) return undefined;
        used.add(variant.id);
        return variant.id;
    });
    const resolvedVariants = productVariants.map((variant, index) => {
        const options = remap(variant.options);
        let id = variantIds[index];
        if (!id) {
            const saved = savedVariants.find((savedVariant) =>
                !used.has(savedVariant.id) &&
                (sameValues(savedVariant.options, variant.options) || sameValues(savedVariant.options, options))
            );
            if (saved) {
                used.add(saved.id);
                id = saved.id;
            }
        }
        return { ...variant, id, options };
    });

    return {
        product_options: sortedOptions.map(({ sourceIndex, ...option }, order) => ({ ...option, order })),
        product_variants: resolvedVariants,
    };
};
